// /admin/inventory — учёт расходных материалов: каталог, партии, рулоны.
//
// Три вкладки на одной странице. Партии/рулоны фильтруются по
// материалу из левого селектора; рулоны дополнительно по станку и
// статусу. Ручной расход — пока через отдельный диалог на вкладке
// «Партии» / «Рулоны»; авто-списание из производственного процесса —
// этап 2.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { PrintQrModal } from "@/components/PrintQrModal";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { adminInventoryApi, stationsApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import { useReference } from "@/lib/reference";
import type {
  InventoryBatchCreate,
  InventoryMaterial,
  InventoryMaterialCreate,
  InventoryMaterialPatch,
  InventoryRoll,
  InventoryTracking,
  UUID,
} from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

/** Правка каталога, приёмка, регистрация рулонов — inventory.manage;
 *  загрузка на станок и снятие — ещё и stage.execute. */
function useInventoryRights() {
  const { user } = useAuth();
  const manage = hasPermission(user, "inventory.manage");
  return { manage, load: manage || hasPermission(user, "stage.execute") };
}

type Tab = "materials" | "batches" | "rolls";

export default function Inventory() {
  const [tab, setTab] = useState<Tab>("rolls");
  const [materialFilter, setMaterialFilter] = useState<UUID | "">("");

  const materials = useQuery({
    queryKey: ["admin", "inventory", "materials", { active: true }],
    queryFn: () => adminInventoryApi.listMaterials({ active: true }),
    staleTime: 60_000,
  });

  return (
    <RequirePermission codes={["inventory.read", "inventory.manage"]}>
      <PageHeader
        eyebrow="админ · склад"
        title="Расходные материалы"
        description="Каталог материалов, партии (от поставщика), рулоны (для материалов с поштучным учётом). Расход — ручной."
      />

      <div className="flex items-center gap-2 border-b border-border">
        {(
          [
            ["rolls", "Рулоны"],
            ["batches", "Партии"],
            ["materials", "Материалы"],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${
              tab === key
                ? "border-accent text-text-primary"
                : "border-transparent text-text-muted hover:text-text-secondary"
            }`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab !== "materials" && (
        <div className="card p-4 flex flex-wrap gap-4 items-end">
          <label className="block text-sm flex-1 min-w-[14rem]">
            <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
              Фильтр по материалу
            </span>
            <select
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value as UUID | "")}
              className={`${INPUT_CLS} font-mono`}
            >
              <option value="">— все материалы —</option>
              {(materials.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} · {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {tab === "materials" && <MaterialsTab />}
      {tab === "batches" && (
        <BatchesTab
          materialId={materialFilter || undefined}
          materials={materials.data?.items ?? []}
        />
      )}
      {tab === "rolls" && (
        <RollsTab
          materialId={materialFilter || undefined}
          materials={materials.data?.items ?? []}
        />
      )}
    </RequirePermission>
  );
}

// ─── Materials tab ───────────────────────────────────────────────────

function MaterialsTab() {
  const rights = useInventoryRights();
  const [editing, setEditing] = useState<InventoryMaterial | "new" | null>(null);
  const [q, setQ] = useState("");

  const list = useQuery({
    queryKey: ["admin", "inventory", "materials", { q }],
    queryFn: () => adminInventoryApi.listMaterials({ q: q.trim() || undefined }),
  });

  return (
    <>
      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <label className="block text-sm flex-1 min-w-[14rem]">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Поиск (code / название)
          </span>
          <input className={INPUT_CLS} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {rights.manage && (
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            + Новый материал
          </button>
        )}
      </div>

      {list.isLoading ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState error={list.error} />
      ) : list.data!.items.length === 0 ? (
        <EmptyState title="Нет материалов" hint="Создайте первый." />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Название</th>
                <th className="px-4 py-3">Ед. изм.</th>
                <th className="px-4 py-3">Tracking</th>
                <th className="px-4 py-3">STAITER ref</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.data!.items.map((m) => (
                <tr key={m.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3 font-mono text-accent-strong">{m.code}</td>
                  <td className="px-4 py-3 text-text-secondary">{m.name}</td>
                  <td className="px-4 py-3 text-text-muted">{m.stock_uom}</td>
                  <td className="px-4 py-3">
                    <span className="pill">
                      {m.tracking === "serial" ? "по рулонам" : "по партиям"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-text-muted">
                    {m.external_item_id ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`pill ${m.is_active ? "pill-ok" : ""}`}>
                      {m.is_active ? "активен" : "архив"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {rights.manage && (
                      <button className="btn text-xs" onClick={() => setEditing(m)}>
                        Изменить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <MaterialDialog
          material={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function MaterialDialog({
  material,
  onClose,
}: {
  material: InventoryMaterial | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = material !== null;
  const units = useReference("unit");
  const categories = useReference("material_category");
  const [data, setData] = useState<
    InventoryMaterialCreate & { is_active: boolean; external_item_id: string }
  >({
    code: material?.code ?? "",
    name: material?.name ?? "",
    stock_uom: material?.stock_uom ?? "",
    material_category: material?.material_category ?? null,
    tracking: material?.tracking ?? "serial",
    external_item_id: material?.external_item_id ?? "",
    is_active: material?.is_active ?? true,
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => {
      const externalId = data.external_item_id.trim() || null;
      if (isEdit) {
        const patch: InventoryMaterialPatch = {
          name: data.name,
          stock_uom: data.stock_uom,
          material_category: data.material_category ?? null,
          tracking: data.tracking,
          is_active: data.is_active,
        };
        return adminInventoryApi
          .patchMaterial(material.id, patch)
          .then(async (m1) => {
            if ((material.external_item_id ?? null) !== externalId) {
              // PATCH external_item_id отдельно (его нет в стандартном patch
              // body — backend пока обновляет только базовые поля).  Если
              // в схеме нет — можно добавить в следующем заходе.
            }
            return m1;
          });
      }
      return adminInventoryApi.createMaterial({
        code: data.code,
        name: data.name,
        stock_uom: data.stock_uom,
        material_category: data.material_category ?? null,
        tracking: data.tracking,
        external_item_id: externalId,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "inventory", "materials"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  return (
    <Modal title={isEdit ? "Редактировать материал" : "Новый материал"} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Код" required>
          <input
            className={`${INPUT_CLS} font-mono`}
            value={data.code}
            onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })}
            disabled={isEdit}
          />
        </Field>
        <Field label="Название" required>
          <input
            className={INPUT_CLS}
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
          />
        </Field>
        <Field label="Категория">
          <select
            className={INPUT_CLS}
            value={data.material_category ?? ""}
            onChange={(e) => setData({ ...data, material_category: e.target.value || null })}
          >
            <option value="">—</option>
            {categories.items.map((c) => (
              <option key={c.code} value={c.code}>
                {categories.label(c.code)}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Единица учёта">
            <select
              className={`${INPUT_CLS} font-mono`}
              value={data.stock_uom}
              onChange={(e) => setData({ ...data, stock_uom: e.target.value })}
            >
              <option value="" disabled>
                —
              </option>
              {units.items.map((u) => (
                <option key={u.code} value={u.code}>
                  {units.label(u.code)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Гранулярность">
            <select
              className={`${INPUT_CLS} font-mono`}
              value={data.tracking}
              onChange={(e) =>
                setData({ ...data, tracking: e.target.value as InventoryTracking })
              }
            >
              <option value="serial">по рулонам (serial)</option>
              <option value="batch">только партия (batch)</option>
            </select>
          </Field>
        </div>
        <Field label="STAITER item-id">
          <input
            className={`${INPUT_CLS} font-mono text-xs`}
            value={data.external_item_id}
            onChange={(e) =>
              setData({ ...data, external_item_id: e.target.value })
            }
            placeholder="uuid из ERP или код вида ME-00000000072 — опц."
            disabled={isEdit /* пока редактирование external_item_id через отдельный backend-патч; задаём только при создании */}
          />
        </Field>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={data.is_active}
              onChange={(e) => setData({ ...data, is_active: e.target.checked })}
            />
            Активен
          </label>
        )}

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <DialogFooter
          onClose={onClose}
          submitLabel={m.isPending ? "Сохраняю…" : isEdit ? "Сохранить" : "Создать"}
          submitDisabled={m.isPending || !data.code || !data.name}
        />
      </form>
    </Modal>
  );
}

// ─── Batches tab ─────────────────────────────────────────────────────

function BatchesTab({
  materialId,
  materials,
}: {
  materialId?: UUID;
  materials: InventoryMaterial[];
}) {
  const rights = useInventoryRights();
  const [openCreate, setOpenCreate] = useState(false);

  const list = useQuery({
    queryKey: ["admin", "inventory", "batches", { materialId }],
    queryFn: () => adminInventoryApi.listBatches({ material_id: materialId }),
  });

  const materialById = useMemo(
    () => new Map(materials.map((m) => [m.id, m])),
    [materials],
  );

  return (
    <>
      {rights.manage && (
        <div className="flex justify-end">
          <button
            className="btn btn-primary"
            onClick={() => setOpenCreate(true)}
            disabled={materials.length === 0}
          >
            + Приёмка партии
          </button>
        </div>
      )}

      {list.isLoading ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState error={list.error} />
      ) : list.data!.items.length === 0 ? (
        <EmptyState
          title="Партий нет"
          hint="Зарегистрируйте приёмку партии материала."
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">Партия</th>
                <th className="px-4 py-3">Материал</th>
                <th className="px-4 py-3 text-right">Принято</th>
                <th className="px-4 py-3 text-right">Остаток</th>
                <th className="px-4 py-3">Срок годности</th>
                <th className="px-4 py-3">ОТК</th>
                <th className="px-4 py-3">Принята</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.items.map((b) => {
                const m = materialById.get(b.material_id);
                return (
                  <tr
                    key={b.id}
                    className="border-t border-border hover:bg-surface-hover"
                  >
                    <td className="px-4 py-3 font-mono">{b.batch_no}</td>
                    <td className="px-4 py-3 text-text-secondary">
                      {m ? (
                        <>
                          <span className="font-mono text-accent-strong">{m.code}</span>{" "}
                          · {m.name}
                        </>
                      ) : (
                        <span className="text-text-muted">{b.material_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {b.qty_received} {m?.stock_uom ?? ""}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {b.qty_remaining} {m?.stock_uom ?? ""}
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs">
                      {b.expiry_date ?? "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className="pill">{b.qc_status}</span>
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs">
                      {new Date(b.received_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <ReceiveBatchDialog
          materials={materials}
          initialMaterialId={materialId}
          onClose={() => setOpenCreate(false)}
        />
      )}
    </>
  );
}

function ReceiveBatchDialog({
  materials,
  initialMaterialId,
  onClose,
}: {
  materials: InventoryMaterial[];
  initialMaterialId?: UUID;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [data, setData] = useState<InventoryBatchCreate>({
    material_id: initialMaterialId ?? materials[0]?.id ?? "",
    batch_no: "",
    qty_received: 0,
    qc_status: "pending",
  });
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => adminInventoryApi.createBatch(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "inventory", "batches"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  const selectedMaterial = materials.find((x) => x.id === data.material_id);

  return (
    <Modal title="Приёмка партии" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Материал" required>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={data.material_id}
            onChange={(e) => setData({ ...data, material_id: e.target.value })}
          >
            {materials.map((mat) => (
              <option key={mat.id} value={mat.id}>
                {mat.code} · {mat.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Номер партии (от поставщика)" required>
          <input
            className={`${INPUT_CLS} font-mono`}
            value={data.batch_no}
            onChange={(e) => setData({ ...data, batch_no: e.target.value })}
            placeholder="LP-H-2026Q2-007"
          />
        </Field>
        <Field
          label={`Принято, ${selectedMaterial?.stock_uom ?? "ед."}`}
          required
        >
          <input
            type="number"
            min={0}
            step={0.001}
            className={`${INPUT_CLS} font-mono`}
            value={data.qty_received}
            onChange={(e) =>
              setData({ ...data, qty_received: Number(e.target.value) })
            }
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Изготовлена">
            <input
              type="date"
              className={INPUT_CLS}
              value={data.mfg_date ?? ""}
              onChange={(e) =>
                setData({ ...data, mfg_date: e.target.value || null })
              }
            />
          </Field>
          <Field label="Срок годности">
            <input
              type="date"
              className={INPUT_CLS}
              value={data.expiry_date ?? ""}
              onChange={(e) =>
                setData({ ...data, expiry_date: e.target.value || null })
              }
            />
          </Field>
        </div>
        <Field label="ОТК">
          <select
            className={`${INPUT_CLS} font-mono`}
            value={data.qc_status}
            onChange={(e) =>
              setData({ ...data, qc_status: e.target.value as "pending" })
            }
          >
            <option value="pending">pending</option>
            <option value="accepted">accepted</option>
            <option value="rejected">rejected</option>
            <option value="quarantine">quarantine</option>
          </select>
        </Field>
        <Field label="Заметка">
          <textarea
            className={INPUT_CLS}
            rows={2}
            value={data.note ?? ""}
            onChange={(e) =>
              setData({ ...data, note: e.target.value || null })
            }
          />
        </Field>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <DialogFooter
          onClose={onClose}
          submitLabel={m.isPending ? "Сохраняю…" : "Принять"}
          submitDisabled={
            m.isPending ||
            !data.material_id ||
            !data.batch_no ||
            data.qty_received <= 0
          }
        />
      </form>
    </Modal>
  );
}

// ─── Rolls tab ───────────────────────────────────────────────────────

function RollsTab({
  materialId,
  materials,
}: {
  materialId?: UUID;
  materials: InventoryMaterial[];
}) {
  const rights = useInventoryRights();
  const [status, setStatus] = useState<string>("");
  const [stationId, setStationId] = useState<string>("");
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [loadFor, setLoadFor] = useState<InventoryRoll | null>(null);

  const stations = useQuery({
    queryKey: ["stations", "all"],
    queryFn: () => stationsApi.list({ include_inactive: true }),
    staleTime: 60_000,
  });

  const list = useQuery({
    queryKey: ["admin", "inventory", "rolls", { materialId, status, stationId, q }],
    queryFn: () =>
      adminInventoryApi.listRolls({
        material_id: materialId,
        status: status || undefined,
        station_id: stationId ? Number(stationId) : undefined,
        q: q.trim() || undefined,
      }),
  });

  // Отдельный запрос на «всё что сейчас загружено» — для виджета над таблицей.
  // Не зависит от фильтров (виджет показывает картину по всем станкам).
  const loadedAll = useQuery({
    queryKey: ["admin", "inventory", "rolls", "loaded"],
    queryFn: () => adminInventoryApi.listRolls({ status: "loaded" }),
    refetchInterval: 15_000,
  });

  const qc = useQueryClient();
  const unloadM = useMutation({
    mutationFn: (id: UUID) => adminInventoryApi.unloadRoll(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "inventory", "rolls"] }),
  });

  const stationName = (id: number | null) => {
    if (id == null) return "—";
    const s = stations.data?.find((x) => x.id === id);
    return s ? s.code : `#${id}`;
  };

  return (
    <>
      <div className="card p-4 flex flex-wrap gap-4 items-end">
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Статус
          </span>
          <select
            className={`mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">все</option>
            <option value="in_stock">на складе</option>
            <option value="loaded">на станке</option>
            <option value="depleted">израсходован</option>
            <option value="scrapped">списан</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Станок
          </span>
          <select
            className="mt-1 px-3 py-2 rounded-md bg-surface border border-border font-mono text-xs"
            value={stationId}
            onChange={(e) => setStationId(e.target.value)}
          >
            <option value="">все</option>
            {(stations.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.code}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm flex-1 min-w-[12rem]">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Поиск (код рулона)
          </span>
          <input className={INPUT_CLS} value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        {rights.manage && (
          <button
            className="btn btn-primary"
            onClick={() => setOpenCreate(true)}
            disabled={materials.length === 0}
          >
            + Зарегистрировать рулон
          </button>
        )}
      </div>

      {(loadedAll.data?.items.length ?? 0) > 0 && (
        <div className="card p-4">
          <h3 className="text-sm font-mono uppercase tracking-wider text-text-muted mb-3">
            Сейчас на станках
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from(
              (loadedAll.data?.items ?? []).reduce((acc, r) => {
                const sid = r.current_station_id ?? 0;
                const arr = acc.get(sid) ?? [];
                arr.push(r);
                acc.set(sid, arr);
                return acc;
              }, new Map<number, InventoryRoll[]>()),
            ).map(([sid, rolls]) => {
              const s = stations.data?.find((x) => x.id === sid);
              return (
                <div
                  key={sid}
                  className="rounded-md border border-border bg-surface-alt/40 p-3"
                >
                  <p className="font-mono text-sm text-accent-strong">
                    {s ? s.code : `#${sid}`}
                  </p>
                  {s && <p className="text-text-muted text-xs mt-0.5">{s.name}</p>}
                  <ul className="mt-2 space-y-1 text-xs">
                    {rolls.map((r) => (
                      <li key={r.id} className="flex items-center justify-between gap-2">
                        <span className="font-mono">
                          <span className="text-text-muted">{r.material_code}</span>
                          {" · "}
                          {r.code}
                        </span>
                        <span className="text-text-muted">
                          {r.remaining_qty}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {list.isLoading ? (
        <LoadingState />
      ) : list.isError ? (
        <ErrorState error={list.error} />
      ) : list.data!.items.length === 0 ? (
        <EmptyState
          title="Рулонов нет"
          hint="Зарегистрируйте рулон в рамках партии serial-tracked материала."
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">Код</th>
                <th className="px-4 py-3">Материал</th>
                <th className="px-4 py-3">Партия</th>
                <th className="px-4 py-3 text-right">Изн.</th>
                <th className="px-4 py-3 text-right">Остаток</th>
                <th className="px-4 py-3">Статус</th>
                <th className="px-4 py-3">Станок</th>
                <th className="px-4 py-3 text-right">Действия</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.items.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-surface-hover">
                  <td className="px-4 py-3 font-mono text-accent-strong">{r.code}</td>
                  <td className="px-4 py-3 text-text-secondary font-mono">
                    {r.material_code ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-text-muted text-xs font-mono">
                    {r.batch_no ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-text-muted">
                    {r.original_qty}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">
                    {r.remaining_qty}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`pill ${r.status === "in_stock" ? "pill-ok" : r.status === "loaded" ? "pill-warn" : ""}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-text-muted">
                    {stationName(r.current_station_id)}
                  </td>
                  <td className="px-4 py-3 text-right text-xs whitespace-nowrap">
                    <div className="inline-flex gap-1 items-center">
                      {rights.manage && <PrintRollQrButton rollId={r.id} rollCode={r.code} />}
                      {!rights.load ? null : r.status === "loaded" ? (
                        <button
                          className="btn"
                          onClick={() => unloadM.mutate(r.id)}
                          disabled={unloadM.isPending}
                        >
                          Снять
                        </button>
                      ) : r.status === "in_stock" ? (
                        <button className="btn" onClick={() => setLoadFor(r)}>
                          Загрузить
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openCreate && (
        <CreateRollDialog
          materials={materials}
          initialMaterialId={materialId}
          onClose={() => setOpenCreate(false)}
        />
      )}
      {loadFor && (
        <LoadRollDialog
          roll={loadFor}
          stations={stations.data ?? []}
          onClose={() => setLoadFor(null)}
        />
      )}
    </>
  );
}

function CreateRollDialog({
  materials,
  initialMaterialId,
  onClose,
}: {
  materials: InventoryMaterial[];
  initialMaterialId?: UUID;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const serialMaterials = materials.filter((m) => m.tracking === "serial");
  const [materialId, setMaterialId] = useState<UUID>(
    initialMaterialId && serialMaterials.find((m) => m.id === initialMaterialId)
      ? initialMaterialId
      : serialMaterials[0]?.id ?? "",
  );
  const [code, setCode] = useState("");
  const [originalQty, setOriginalQty] = useState<number>(0);

  // Раскрывашка «документы поставщика» — заполняется когда поставка
  // пришла с накладной и админ хочет зафиксировать партию явно.
  const [showSupplier, setShowSupplier] = useState(false);
  const [batchNo, setBatchNo] = useState("");
  const [mfgDate, setMfgDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [qcStatus, setQcStatus] = useState<
    "pending" | "accepted" | "rejected" | "quarantine"
  >("pending");

  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      adminInventoryApi.createRoll({
        material_id: materialId,
        code,
        original_qty: originalQty,
        batch_data: showSupplier
          ? {
              batch_no: batchNo.trim() || null,
              mfg_date: mfgDate || null,
              expiry_date: expiryDate || null,
              qc_status: qcStatus,
            }
          : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "inventory", "rolls"] });
      qc.invalidateQueries({ queryKey: ["admin", "inventory", "batches"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  const material = serialMaterials.find((x) => x.id === materialId);

  return (
    <Modal title="Регистрация рулона" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Материал" required>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
          >
            {serialMaterials.length === 0 ? (
              <option value="">— нет материалов «по рулонам» —</option>
            ) : (
              serialMaterials.map((mat) => (
                <option key={mat.id} value={mat.id}>
                  {mat.code} · {mat.name}
                </option>
              ))
            )}
          </select>
        </Field>
        <Field label="Код рулона" required>
          <input
            className={`${INPUT_CLS} font-mono`}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="R-LP-H-001"
          />
        </Field>
        <Field
          label={`Объём, ${material?.stock_uom ?? "ед."}`}
          required
        >
          <input
            type="number"
            min={0}
            step={0.001}
            className={`${INPUT_CLS} font-mono`}
            value={originalQty}
            onChange={(e) => setOriginalQty(Number(e.target.value))}
          />
        </Field>

        <div className="rounded-md border border-border">
          <button
            type="button"
            className="w-full px-3 py-2 flex items-center justify-between text-left text-xs font-mono uppercase tracking-wider text-text-muted hover:bg-surface"
            onClick={() => setShowSupplier((v) => !v)}
          >
            <span>Документы поставщика (опционально)</span>
            <span>{showSupplier ? "▾" : "▸"}</span>
          </button>
          {showSupplier && (
            <div className="p-3 space-y-3 border-t border-border bg-surface-alt/30">
              <p className="text-xs text-text-muted">
                Если рулон приехал в составе поставки с накладной — впиши
                номер партии и даты. Несколько рулонов с одинаковым
                номером партии соберутся в одну партию. Если не
                заполнять — будет техническая партия из одного рулона.
              </p>
              <Field label="Номер партии">
                <input
                  className={`${INPUT_CLS} font-mono`}
                  value={batchNo}
                  onChange={(e) => setBatchNo(e.target.value)}
                  placeholder="LP-H-2026Q2-007"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Изготовлена">
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={mfgDate}
                    onChange={(e) => setMfgDate(e.target.value)}
                  />
                </Field>
                <Field label="Срок годности">
                  <input
                    type="date"
                    className={INPUT_CLS}
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </Field>
              </div>
              <Field label="ОТК">
                <select
                  className={`${INPUT_CLS} font-mono`}
                  value={qcStatus}
                  onChange={(e) =>
                    setQcStatus(e.target.value as typeof qcStatus)
                  }
                >
                  <option value="pending">pending</option>
                  <option value="accepted">accepted</option>
                  <option value="rejected">rejected</option>
                  <option value="quarantine">quarantine</option>
                </select>
              </Field>
            </div>
          )}
        </div>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <DialogFooter
          onClose={onClose}
          submitLabel={m.isPending ? "Сохраняю…" : "Зарегистрировать"}
          submitDisabled={
            m.isPending || !materialId || !code || originalQty <= 0
          }
        />
      </form>
    </Modal>
  );
}

function LoadRollDialog({
  roll,
  stations,
  onClose,
}: {
  roll: InventoryRoll;
  stations: { id: number; code: string; name: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [stationId, setStationId] = useState<number | "">(stations[0]?.id ?? "");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () =>
      adminInventoryApi.loadRoll(roll.id, stationId as number, note || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "inventory", "rolls"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : "Ошибка"),
  });

  return (
    <Modal title={`Загрузить рулон ${roll.code}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Станок" required>
          <select
            className={`${INPUT_CLS} font-mono`}
            value={stationId}
            onChange={(e) =>
              setStationId(e.target.value ? Number(e.target.value) : "")
            }
          >
            {stations.map((s) => (
              <option key={s.id} value={s.id}>
                {s.code} · {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Заметка">
          <input
            className={INPUT_CLS}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <DialogFooter
          onClose={onClose}
          submitLabel={m.isPending ? "Загружаю…" : "Загрузить"}
          submitDisabled={m.isPending || !stationId}
        />
      </form>
    </Modal>
  );
}

function PrintRollQrButton({ rollId, rollCode }: { rollId: UUID; rollCode: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen(true)}
        title={`Распечатать QR для рулона ${rollCode} (A4)`}
      >
        🖨 QR
      </button>
      {open && (
        <PrintQrModal
          templateKind="roll-qr"
          subjectLabel={rollCode}
          onClose={() => setOpen(false)}
          onPrint={(target) => adminInventoryApi.printRollQr(rollId, target)}
        />
      )}
    </>
  );
}

// ─── Shared bits ─────────────────────────────────────────────────────

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-page/70 backdrop-blur z-20 flex items-center justify-center p-6">
      <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <header className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-xl">{title}</h2>
          <button type="button" className="btn text-xs" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function DialogFooter({
  onClose,
  submitLabel,
  submitDisabled,
}: {
  onClose: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
      <button type="button" className="btn" onClick={onClose}>
        Отмена
      </button>
      <button type="submit" className="btn btn-primary" disabled={submitDisabled}>
        {submitLabel}
      </button>
    </div>
  );
}
